import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import API from '../../API/Main/API';
import {
	GetSessionTypeProperties,
	SessionDataProperties,
	SupervisedSessionInfoProperties,
	TargetSessionStatuses,
	UnlockSessionProperties,
	UpdateSessionProperties,
} from '../../API/ClientAPIHelpers/sessionsProperties';
import {
	GetSessionProperties,
	SessionProperties,
	SessionTypeProperties,
} from '../../API/ClientAPIHelpers/calendarProperties';
import { ErrorProperties } from '../../API/identityAPIProperties';
import { SessionTargetsProperties, SessionTrialProperties } from '../../API/ClientAPIHelpers/dataCollectionProperties';
import {
	ClientTargetProperties,
	GetClientTargetsProperties,
	UpdateTargetStepsProperties,
} from '../../API/ClientAPIHelpers/programTargetsProperties';
import { LocationTypes } from '../../API/ClientAPIHelpers/soapNoteProperties';

export interface SupervisedSessionProperties extends GetSessionProperties {
	location: LocationTypes;
}

export interface CustomTargetProperties extends SessionTrialProperties {
	id: string;
	name: string;

	orderId: number;
	programId: string;
	programName: string;
	sd: string;
	steps: UpdateTargetStepsProperties[] | null;
	targetInstructions: string;
	targetStatus: TargetSessionStatuses;
	type: number;
}

interface ActiveSessionStateProperties {
	activeClientTargets: ClientTargetProperties[];
	error: ErrorProperties;
	loading: boolean;
	originalEndTime: string;
	originalStartTime: string;
	sessionInfo: GetSessionProperties;
	sessionTargets: SessionTargetsProperties[];
	sessionTypes: SessionTypeProperties[];
	supervisedSessionInfo: SupervisedSessionProperties[];
	supervisorSessionInfo: SupervisedSessionInfoProperties[];
	targets: CustomTargetProperties[];
}

const initialSessionState: ActiveSessionStateProperties = {
	loading: false,
	targets: [],
	sessionTypes: [],
	originalStartTime: '',
	originalEndTime: '',
	supervisedSessionInfo: [],
	supervisorSessionInfo: [],
	sessionInfo: {
		id: '',
		client: {
			id: '',
			fullName: '',
			dateOfBirthday: '',
			email: '',
			gender: '',
			defaultBcba: {
				id: '',
				fullName: '',
				firstName: '',
				lastName: '',
				email: '',
				role: {
					id: '',
					name: '',
					section: {
						id: 0,
						name: '',
					},
				},
			},
		},
		user: {
			id: '',
			fullName: '',
			firstName: '',
			lastName: '',
			email: '',
			role: {
				id: '',
				name: '',
				section: {
					id: 0,
					name: '',
				},
			},
		},
		sessionType: {
			id: 0,
			name: '',
			type: 0,
			allowedRoleSections: [],
			funderId: null,
			requiresSoapNote: true,
		},
		reportStatus: {
			status: 0,
			name: '',
		},
		date: '',
		startTime: '',
		endTime: '',
		isCompleted: false,
		isSessionFlagged: false,
		reportId: '',
		sessionId: '',
		title: '',
		type: '',
	},
	sessionTargets: [],
	activeClientTargets: [],
	error: {
		status: 0,
		title: '',
	},
};

export const startEventSession = createAsyncThunk('/calendar/start-session', async (eventId: string, thunkAPI) => {
	const session = await API.ClientAPI.Sessions.startEventSession(eventId);
	const response = { ...session, sessionId: session.id };

	if (response.error) {
		return thunkAPI.rejectWithValue(response.error);
	}

	return response as SessionProperties;
});

export const getSessionTypes = createAsyncThunk('/sessions', async (data: GetSessionTypeProperties, thunkAPI) => {
	const response = await API.ClientAPI.Sessions.getSessionTypes(data);

	if (response.error) {
		return thunkAPI.rejectWithValue(response.error);
	}

	return response;
});

export const getSupervisedSessionsInfo = createAsyncThunk(
	'/sessions/get/info/supervised',
	async (sessionId: string, thunkAPI) => {
		const response = await API.ClientAPI.Sessions.getSupervisedSessionsInfo(sessionId);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

export const getSupervisorSessionsInfo = createAsyncThunk(
	'/sessions/get/info/supervisor',
	async (sessionId: string, thunkAPI) => {
		const response = await API.ClientAPI.Sessions.getSupervisorSessionsInfo(sessionId);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

export const startSession = createAsyncThunk('/sessions/start', async (data: SessionDataProperties, thunkAPI) => {
	const response = await API.ClientAPI.Sessions.startSession(data);

	if (response.error) {
		return thunkAPI.rejectWithValue(response.error);
	}

	return response as SessionProperties;
});

// export const endSession = createAsyncThunk(
//     '/sessions/end',
//     async (sessionId: string, thunkAPI) => {
//         const response = await API.ClientAPI.Sessions.endSession(sessionId);
//         if (!!response.error) {
//             return thunkAPI.rejectWithValue(response.error);
//         }
//         return response as SessionProperties;
//     }
// );

export const getSessionInfo = createAsyncThunk('/sessions/get/info', async (sessionId: string, thunkAPI) => {
	const response = await API.ClientAPI.Sessions.getSessionInfo(sessionId);

	if (response.error) {
		return thunkAPI.rejectWithValue(response.error);
	}

	return response as GetSessionProperties;
});

// Completes an ELC session without a SOAP note (data-collection only, no billing/note).
export const endSession = createAsyncThunk('/sessions/end', async (sessionId: string, thunkAPI) => {
	const response = await API.ClientAPI.Sessions.endSession(sessionId);

	if (response.error) {
		return thunkAPI.rejectWithValue(response.error);
	}

	return response;
});

export const getSessionTargets = createAsyncThunk('/sessions/targets/all', async (sessionId: string, thunkAPI) => {
	const response = await API.ClientAPI.DataCollection.sessions.targets.getSessionTargets(sessionId);

	if (response.error) {
		return thunkAPI.rejectWithValue(response.error);
	}

	return response as SessionTargetsProperties[];
});

export const getAllActiveClientTargets = createAsyncThunk(
	'/client/targets/all/active',
	async (data: GetClientTargetsProperties, thunkAPI) => {
		const response = await API.ClientAPI.ProgramTargets.client.getClientTargets(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as ClientTargetProperties[];
	},
);

export const activateSession = createAsyncThunk('/sessions/activate', async (sessionId: string, thunkAPI) => {
	const response = await API.ClientAPI.Sessions.activateSession(sessionId);

	if (response.error) {
		return thunkAPI.rejectWithValue(response.error);
	}

	return response;
});

export const unlockSession = createAsyncThunk('/sessions/unlock', async (data: UnlockSessionProperties, thunkAPI) => {
	const response = await API.ClientAPI.Sessions.unlockSession(data);

	if (response.error) {
		return thunkAPI.rejectWithValue(response.error);
	}

	return response;
});

export const updateSession = createAsyncThunk(
	'/sessions/update',
	async ({ data, sessionId }: { data: UpdateSessionProperties; sessionId: string }, thunkAPI) => {
		const response = await API.ClientAPI.Sessions.updateSession(sessionId, data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

export const createSupervisedBehaviorTreatmentSession = createAsyncThunk(
	'/sessions/bt/supervised',
	async ({ userId, sessionId }: { sessionId: string; userId: string }, thunkAPI) => {
		const response = await API.ClientAPI.Sessions.createBehaviorTreatmentSession(sessionId, userId);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as GetSessionProperties;
	},
);

const SessionSlice = createSlice({
	name: 'session',
	initialState: initialSessionState,
	reducers: {
		setTargets(state, action) {
			state.targets = action.payload;
		},
		clearTargets(state) {
			state.targets = initialSessionState.targets;
		},
		setOpenTargetNew(state, action) {
			const id = action.payload;
			const newTargets = state.targets.map((x) => {
				if (x.id !== id) return x;

				return { ...x, targetStatus: TargetSessionStatuses.new };
			});
			state.targets = newTargets;
		},
		setActiveTargetHidden(state, action) {
			const id = action.payload;
			const newTargets = state.targets.map((x) => {
				if (x.id !== id) return x;

				return { ...x, targetStatus: TargetSessionStatuses.hidden };
			});
			state.targets = newTargets;
		},
		setHiddenTargetActive(state, action) {
			const id = action.payload;
			const newTargets = state.targets.map((x) => {
				if (x.id !== id) return x;

				return { ...x, targetStatus: TargetSessionStatuses.active };
			});
			state.targets = newTargets;
		},
		setDate(state, action) {
			// Wall-clock date only (YYYY-MM-DD). The server returns naive ISO strings;
			// slice(0,10) is a safe fallback if an offset ever sneaks in.
			state.sessionInfo.date = action.payload ? action.payload.slice(0, 10) : '';
		},
		setStartTime(state, action) {
			// Wall-clock datetime (YYYY-MM-DDTHH:mm:ss). Slice keeps the first 19
			// chars of any ISO string, discarding any trailing Z or ±HH:mm offset.
			state.sessionInfo.startTime = action.payload.slice(0, 19);
		},
		setEndTime(state, action) {
			state.sessionInfo.endTime = action.payload.slice(0, 19);
		},
		setSessionInfoReportId(state, action) {
			state.sessionInfo.reportId = action.payload;
		},
		setSessionInfoUser(state, action) {
			state.sessionInfo.user = action.payload;
		},
		clearSessionInfo(state) {
			state.sessionInfo = initialSessionState.sessionInfo;
			state.originalStartTime = initialSessionState.originalStartTime;
			state.originalEndTime = initialSessionState.originalEndTime;
		},
	},
	extraReducers: (builder) => {
		// unlockSession
		builder.addCase(unlockSession.pending, (state) => {
			state.loading = true;
			state.error = initialSessionState.error;
		});
		builder.addCase(unlockSession.fulfilled, (state) => {
			state.loading = false;
		});
		builder.addCase(unlockSession.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});

		// activateSession
		builder.addCase(activateSession.pending, (state) => {
			state.loading = true;
			state.error = initialSessionState.error;
		});
		builder.addCase(activateSession.fulfilled, (state) => {
			state.loading = false;
		});
		builder.addCase(activateSession.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});

		// updateSession
		builder.addCase(updateSession.pending, (state) => {
			state.loading = true;
			state.error = initialSessionState.error;
		});
		builder.addCase(updateSession.fulfilled, (state) => {
			state.loading = false;
		});
		builder.addCase(updateSession.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});

		// endSession
		// builder.addCase(endSession.pending, (state) => {
		//     state.loading = true;
		//     state.error = initialSessionState.error;
		// });
		// builder.addCase(endSession.fulfilled, (state) => {
		//     state.loading = false;
		// });
		// builder.addCase(endSession.rejected, (state, action) => {
		//     state.loading = false;
		//     state.error = action.payload as ErrorProperties;
		// });

		// get sessions types
		builder.addCase(getSessionTypes.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(getSessionTypes.fulfilled, (state, action) => {
			state.loading = false;
			state.sessionTypes = [{ id: 0, type: 0, name: 'Select session type', funderId: null }, ...action.payload];
		});
		builder.addCase(getSessionTypes.rejected, (state, action) => {
			state.loading = false;
			state.error = action.error as ErrorProperties;
		});
		// getSessionInfo
		builder.addCase(getSessionInfo.pending, (state) => {
			state.loading = true;
			state.error = initialSessionState.error;
		});
		builder.addCase(getSessionInfo.fulfilled, (state, action) => {
			state.loading = false;
			// Keep only the wall-clock portion (YYYY-MM-DDTHH:mm:ss), discarding
			// any trailing timezone offset the server may include.
			const startTime = action.payload.startTime.slice(0, 19);
			const endTime = action.payload.endTime.slice(0, 19);
			state.sessionInfo = {
				...action.payload,
				startTime,
				endTime,
			};
			state.originalStartTime = startTime;
			state.originalEndTime = endTime;
		});
		builder.addCase(getSessionInfo.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});

		// getSupervisedSessionsInfo
		builder.addCase(getSupervisedSessionsInfo.pending, (state) => {
			state.loading = true;
			state.error = initialSessionState.error;
		});
		builder.addCase(getSupervisedSessionsInfo.fulfilled, (state, action) => {
			state.loading = false;
			state.supervisedSessionInfo = action.payload;
		});
		builder.addCase(getSupervisedSessionsInfo.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});

		// getSupervisorSessionsInfo
		builder.addCase(getSupervisorSessionsInfo.pending, (state) => {
			state.loading = true;
			state.error = initialSessionState.error;
		});
		builder.addCase(getSupervisorSessionsInfo.fulfilled, (state, action) => {
			state.loading = false;
			state.supervisorSessionInfo = action.payload;
		});
		builder.addCase(getSupervisorSessionsInfo.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});

		// getSessionTargets
		builder.addCase(getSessionTargets.pending, (state) => {
			state.loading = true;
			state.error = initialSessionState.error;
		});
		builder.addCase(getSessionTargets.fulfilled, (state, action) => {
			state.loading = false;
			const targets: CustomTargetProperties[] = action.payload.map((x, index) => ({
				...x,
				id: x.target.id,
				orderId: index,
				targetStatus: TargetSessionStatuses.active,
				name: x.target.name,
				steps: x.target.steps,
				type: x.target.type,
				programId: x.program.id,
				programName: x.program.name,
				targetInstructions: x.target.targetInstructions,
				sd: x.target.sd,
			}));
			state.sessionTargets = action.payload;
			const activeTargets = state.targets.map((x, index) => {
				const activeTarget = targets.find((y) => x.id === y.id);
				if (activeTarget)
					return {
						...activeTarget,
						orderId: state.targets[index].orderId,
						targetStatus:
							state.targets[index].targetStatus === TargetSessionStatuses.hidden
								? state.targets[index].targetStatus
								: activeTarget.targetStatus,
					};

				return x;
			});
			state.targets = activeTargets;
		});
		builder.addCase(getSessionTargets.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});

		// getAllActiveClientTargets
		builder.addCase(getAllActiveClientTargets.pending, (state) => {
			state.loading = true;
			state.error = initialSessionState.error;
		});
		builder.addCase(getAllActiveClientTargets.fulfilled, (state, action) => {
			state.loading = false;
			const targets: CustomTargetProperties[] = action.payload.map((x) => ({
				id: x.id,
				orderId: -1,
				targetStatus: TargetSessionStatuses.new,
				name: x.name,
				steps: null,
				type: x.type.id,
				programId: x.program.id,
				programName: x.program.name,
				trials: 0,
				independent: 0,
				prompt: 0,
				correctPercentage: 0,
				targetInstructions: x.targetInstructions,
				sd: x.sd,
			}));
			state.activeClientTargets = action.payload;
			state.targets = targets;
		});
		builder.addCase(getAllActiveClientTargets.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
	},
});

export const {
	setDate,
	setEndTime,
	setStartTime,
	setSessionInfoReportId,
	setSessionInfoUser,
	setTargets,
	clearTargets,
	setOpenTargetNew,
	setActiveTargetHidden,
	setHiddenTargetActive,
	clearSessionInfo,
} = SessionSlice.actions;

export default SessionSlice.reducer;
